import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import type { SnackbarContextType } from '@/contexts/SnackbarContext.types';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';
import { handleSnackbarError } from '@/shared/utils/errorHandler';

export interface UserDistributorGroup {
  userId: string;
  distributorGroupId: number;
  createdUtc: string;
}

type AddUsersToDistributorGroupRequest = components['schemas']['AddUsersToDistributorGroupEndpoint_AddUsersRequest'];
type ChangeUsersDistributorRoleRequestBody =
  components['schemas']['ChangeUserDistributorRoleEndpoint_ChangeUsersDistributorRoleRequestBody'];
type DistributorSnapshot = components['schemas']['AddUsersToDistributorGroupEndpoint_DistributorSnapshotRequest'];

interface AddUsersParams {
  distributorGroupId: number;
  userIds: string[];
  distributor: DistributorSnapshot;
}
interface RemoveUsersBatchParams {
  distributorGroupId: number;
  users: {
    userId: string;
    role: string;
  }[];
}

interface RemoveDistributorsBatchParams {
  email: string;
  groups: { distributorGroupId: number; role: string }[];
}

interface UseManageDistributorGroupUsersReturn {
  addUsers: (distributorGroupId: number, userIds: string[], distributor: DistributorSnapshot) => Promise<UserDistributorGroup[] | null>;
  removeUser: (distributorGroupId: number, userId: string, role: string) => Promise<boolean>;
  removeUsers: (distributorGroupId: number, users: { userId: string; role: string }[]) => Promise<boolean>;
  removeDistributors: (email: string, groups: { distributorGroupId: number; role: string }[]) => Promise<boolean>;
  updateUser: (distributorGroupId: number, userId: string, role: string) => Promise<UserDistributorGroup | null>;
  isMutating: boolean;
  error: string | null;
  removeUserMutation: UseMutationResult<
    boolean,
    unknown,
    {
      distributorGroupId: number;
      userId: string;
      role: string;
    },
    unknown
  >;
  removeUsersBatchMutation: UseMutationResult<boolean, unknown, RemoveUsersBatchParams, unknown>;
  removeDistributorsBatchMutation: UseMutationResult<boolean, unknown, RemoveDistributorsBatchParams, unknown>;
  updateUserMutation: UseMutationResult<
    UserDistributorGroup,
    unknown,
    {
      distributorGroupId: number;
      userId: string;
      role: string;
    },
    unknown
  >;
}

interface UseManageDistributorGroupUsersConfig {
  snackbar: SnackbarContextType;
}

export function useManageDistributorGroupUsers({ snackbar }: UseManageDistributorGroupUsersConfig): UseManageDistributorGroupUsersReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  const addUsersMutation = useMutation({
    mutationFn: async (params: AddUsersParams) => {
      const requestBody: AddUsersToDistributorGroupRequest = {
        userIds: params.userIds,
        distributor: params.distributor,
      };

      const client = createApiClient(getAccessToken);

      const result = await client.POST('/api/organizations/{organizationId}/distributor-groups/{distributorGroupId}/users', {
        params: {
          path: { organizationId, distributorGroupId: params.distributorGroupId },
        },
        body: requestBody,
      });

      return result.data && Array.isArray(result.data.successes) ? (result.data.successes as UserDistributorGroup[]) : null;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries(
          { queryKey: ['distributor-groups-autocomplete', organizationId], exact: false },
          { cancelRefetch: false },
        ),
        queryClient.invalidateQueries(
          { queryKey: ['user-distributor-group-membership', organizationId], exact: false },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const addUsers = async (
    distributorGroupId: number,
    userIds: string[],
    distributor: DistributorSnapshot,
  ): Promise<UserDistributorGroup[] | null> => {
    try {
      const result = await addUsersMutation.mutateAsync({
        distributorGroupId,
        userIds,
        distributor,
      });
      return result;
    } catch {
      return null;
    }
  };

  const removeUserMutation = useMutation({
    mutationFn: async ({
      distributorGroupId,
      userId,
      role,
    }: {
      distributorGroupId: number;
      userId: string;
      role: string;
    }): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const result = await client.DELETE('/api/organizations/{organizationId}/distributor-groups/{distributorGroupId}/users', {
        params: {
          path: { organizationId, distributorGroupId },
          query: {
            UserId: userId,
            Role: role,
          },
        },
      });

      return result.response.status === 204 || result.response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['distributorGroupByUser', organizationId], exact: false }, { cancelRefetch: false }),
        queryClient.invalidateQueries(
          { queryKey: ['user-distributor-group-membership', organizationId], exact: false },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const removeUser = async (distributorGroupId: number, userId: string, role: string): Promise<boolean> => {
    try {
      return await removeUserMutation.mutateAsync({ distributorGroupId, userId, role });
    } catch {
      return false;
    }
  };

  const removeUsersBatchMutation = useMutation({
    mutationFn: async (params: RemoveUsersBatchParams): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const result = await client.DELETE('/api/organizations/{organizationId}/distributor-groups/{distributorGroupId}/users/batch', {
        params: {
          path: { organizationId, distributorGroupId: params.distributorGroupId },
        },
        body: {
          users: params.users,
        },
      });

      return result.response.status === 204 || result.response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['distributorGroupByUser', organizationId], exact: false }, { cancelRefetch: false }),
        queryClient.invalidateQueries(
          { queryKey: ['user-distributor-group-membership', organizationId], exact: false },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      handleSnackbarError(err, snackbar, 'Failed to remove users');
    },
  });

  const removeUsers = async (distributorGroupId: number, users: { userId: string; role: string }[]): Promise<boolean> => {
    try {
      return await removeUsersBatchMutation.mutateAsync({ distributorGroupId, users });
    } catch {
      return false;
    }
  };

  const removeDistributorsBatchMutation = useMutation({
    mutationFn: async (params: RemoveDistributorsBatchParams): Promise<boolean> => {
      const client = createApiClient(getAccessToken);

      const result = await client.DELETE('/api/organizations/{organizationId}/users/distributor-groups/batch', {
        params: {
          path: { organizationId },
        },
        body: {
          userId: params.email,
          distributorGroups: params.groups,
        },
      });

      return result.response.status === 204 || result.response.ok;
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['distributorGroupByUser', organizationId], exact: false }, { cancelRefetch: false }),
        queryClient.invalidateQueries(
          { queryKey: ['user-distributor-group-membership', organizationId], exact: false },
          { cancelRefetch: false },
        ),
      ]);
    },
    onError: (err: unknown) => {
      handleSnackbarError(err, snackbar, 'Failed to remove distributor groups from user');
    },
  });

  const removeDistributors = async (email: string, groups: { distributorGroupId: number; role: string }[]): Promise<boolean> => {
    try {
      return await removeDistributorsBatchMutation.mutateAsync({ email, groups });
    } catch {
      return false;
    }
  };

  const updateUserMutation = useMutation({
    mutationFn: async ({
      distributorGroupId,
      userId,
      role,
    }: {
      distributorGroupId: number;
      userId: string;
      role: string;
    }): Promise<UserDistributorGroup> => {
      const requestBody: ChangeUsersDistributorRoleRequestBody = {
        role,
      };

      const client = createApiClient(getAccessToken);

      const result = await client.PUT('/api/organizations/{organizationId}/distributor-groups/{distributorGroupId}/users', {
        params: {
          path: { organizationId, distributorGroupId },
          query: { UserId: userId },
        },
        body: requestBody,
      });

      return result.data as UserDistributorGroup;
    },
    onSuccess: () => {
      return queryClient.invalidateQueries(
        { queryKey: ['distributorGroupByUser', organizationId], exact: false },
        { cancelRefetch: false },
      );
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Unknown error occurred';
      handleSnackbarError(err, snackbar, message);
    },
  });

  const updateUser = async (distributorGroupId: number, userId: string, role: string): Promise<UserDistributorGroup | null> => {
    try {
      return await updateUserMutation.mutateAsync({ distributorGroupId, userId, role });
    } catch {
      return null;
    }
  };

  const isMutating =
    addUsersMutation.isPending ||
    removeUserMutation.isPending ||
    removeUsersBatchMutation.isPending ||
    removeDistributorsBatchMutation.isPending ||
    updateUserMutation.isPending;

  const error =
    addUsersMutation.error instanceof Error
      ? addUsersMutation.error.message
      : removeUserMutation.error instanceof Error
        ? removeUserMutation.error.message
        : removeUsersBatchMutation.error instanceof Error
          ? removeUsersBatchMutation.error.message
          : removeDistributorsBatchMutation.error instanceof Error
            ? removeDistributorsBatchMutation.error.message
            : updateUserMutation.error instanceof Error
              ? updateUserMutation.error.message
              : null;

  return {
    addUsers,
    removeUser,
    removeUsers,
    removeDistributors,
    updateUser,
    isMutating,
    error,
    removeUserMutation,
    removeUsersBatchMutation,
    removeDistributorsBatchMutation,
    updateUserMutation,
  };
}
